import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { AbstractControl } from '@angular/forms';

import { take } from 'rxjs';

import { NgRecaptcha3Service } from 'ng-recaptcha3';

import { CardSetTransformationService, CoreService, DataService, DialogResult, DialogService, FormNavigationService, SpinnerService, VerticalNavigationSettings } from 'dspx-core';

import { API_ENDPOINT_KEYS, API_ENDPOINTS } from '../../constants/api-endpoints.constants';
import { TEMPLATES, VERSIONS } from '../../constants/templates.constants';
import { APP_DATA_KEYS, FORM_ACTIONS, HIDEABLE_GRIDS, NAV_TAB_NUMBERS, PAGE_NUMBERS, POP_UP_CODES, QUESTION_CODES, READ_ONLY_ACCESS_PAGE_ACTION_BUTTONS, SECTION_CODES, SERVER_ERROR_POP_UP_CODES } from '../../constants/app-data.constants';
import { ROUTE_CONSTANTS } from '../../constants/route.constants';

import { TypeaheadQuestionModel } from '../../interfaces/typeahead-question-model.interface';
import { EntraIdUser } from '../../interfaces/entra-id-user.interface';
import { CoiFormSaveRequest } from '../../interfaces/coi-form-save-request.interface';

import { ErrorService } from '../../services/error.service';
import { AppDataService } from '../../services/app-data.service';
import { FormDataService, HideableGrid } from '../../services/form-data.service';
import { FormConfigurationParserService } from '../../services/form-configuration-parser.service';
import { QuestionPath, ResponseParserService } from '../../services/response-parser.service';
import { HtmlHelperService } from '../../services/html-helper.service';
import { JsonSerialisationService } from '../../services/json-serialisation.service';

import { BaseCardSetDetailComponent } from '../base-card-set-detail-component/base-card-set-detail.component';

@Component({
  selector: 'app-coi-form',
  templateUrl: './coi-form.component.html',
  styleUrl: './coi-form.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class CoiFormComponent extends BaseCardSetDetailComponent implements OnInit, OnDestroy {

  fieldTypeReplacements = CardSetTransformationService.cardSetWithFlatTabsFieldTypeReplacements;
  verticalNavigationSettings: VerticalNavigationSettings = { enabled: true, sticky: true, pageLevel: true };
  id = '';
  formServiceEndpoint: string = API_ENDPOINT_KEYS.getCoiFormConfigWithEmptyResponse;
  templateId: number = TEMPLATES.coiForm;
  versionNo: number = VERSIONS.latest;
  activeDialog: string;
  editing = false;
  isExternalRoute = false;
  isViewRoute = false;

  private versionHistoryHideableGrid: HideableGrid;
  private approver: EntraIdUser;

  constructor(
    protected router: Router,
    protected appDataService: AppDataService,
    protected coreService: CoreService,
    protected formDataService: FormDataService,
    protected htmlHelperService: HtmlHelperService,
    protected dialogService: DialogService,
    private route: ActivatedRoute,
    private recaptchaService: NgRecaptcha3Service,
    private errorService: ErrorService,
    private dataService: DataService,
    private spinnerService: SpinnerService,
    private formConfigurationParserService: FormConfigurationParserService,
    private responseParserService: ResponseParserService,
    private jsonSerialisationService: JsonSerialisationService,
    private formNavigationService: FormNavigationService) {
    super(router, appDataService, coreService, formDataService, htmlHelperService, dialogService);
  }

  ngOnDestroy(): void {
    this.unsubscribeSubscriptions();
    this.coreService.applicationFunctions = {};
    this.coreService.repeatingPageFunctions = {};
    this.coreService.repeatingSectionFunctions = {};
  }

  ngOnInit(): void {
    this.registerSubscription(
      this.route.paramMap.subscribe((paramMap: ParamMap) => {
        const id = paramMap.get('id');
        if (id) {
          this.id = id;
          this.isExternalRoute = this.router.url === `/${ROUTE_CONSTANTS.coiForm.externalPrefix}/${id}` || this.router.url === `/${ROUTE_CONSTANTS.coiForm.externalViewPrefix}/${id}`;
          this.isViewRoute = this.router.url === `/${ROUTE_CONSTANTS.coiForm.viewPrefix}/${id}` || this.router.url === `/${ROUTE_CONSTANTS.coiForm.externalViewPrefix}/${id}`;
          this.formServiceEndpoint = this.isExternalRoute
            ? this.isViewRoute
              ? API_ENDPOINT_KEYS.getCoiFormConfigAndResponseExternalReadOnly
              : API_ENDPOINT_KEYS.getCoiFormConfigAndResponseExternal
            : this.isViewRoute
              ? API_ENDPOINT_KEYS.getCoiFormConfigAndResponseReadOnly
              : API_ENDPOINT_KEYS.getCoiFormConfigAndResponse;
          this.editing = true;
        }
        else {
          this.resetDirtyFlagAfterLoading();
        }
      })
    );

    this.formNavigationService.onNavigation().subscribe(event => {
      setTimeout(() => {
        this.overrideHtmlTitleSetByDspxFramework();
      })
    });

    this.coreService.conditionFunctions['isEditing'] = () => this.editing;
    this.coreService.conditionFunctions['isReadOnly'] = () => true;

    this.coreService.applicationFunctions['canSave'] = () => this.isFormEditable;
    this.coreService.applicationFunctions['canDelete'] = () => this.isViewRoute;
    this.coreService.applicationFunctions['downloadCopy'] = () => this.downloadCopy();
    this.coreService.applicationFunctions['reopenWithComment'] = () => this.openDialogForReopenWithComment();
    this.coreService.applicationFunctions['reopen'] = () => this.reopen();
    this.coreService.applicationFunctions['delete'] = () => this.delete(null);
    this.coreService.applicationFunctions['canSwitchToEdit'] = () => this.isViewRoute;
    this.coreService.applicationFunctions['switchToEdit'] = () => this.switchToEdit(null);

    this.versionHistoryHideableGrid = this.initialiseHideableGrid(HIDEABLE_GRIDS.coiForm.versionHistory);
  }

  formLoaded(event: any): void {
    if (event.retryAsReadOnly) {
      const viewPrefix = this.isExternalRoute
        ? ROUTE_CONSTANTS.coiForm.externalViewPrefix
        : ROUTE_CONSTANTS.coiForm.viewPrefix;
      this.navigate(['/', viewPrefix, this.id], true);
      return;
    }

    if (this.isRecaptchaRequired()) {
      const recaptchaSiteKey = this.appDataService.getAppData(APP_DATA_KEYS.googleRecaptchaSiteKey);
      this.recaptchaService.init(recaptchaSiteKey).then(status => {
        console.log('reCAPTCHA service init: ' + status);
      });
    }

    this.isFormEditable = !!event?.isFormEditable;

    setTimeout(() => this.handleManagementPlanNavTabChange()); // the controls are populated in the next tick, so we need to wait for that

    if (this.baseForm && this.baseForm.model) {
      this.appDataService.setAppData(APP_DATA_KEYS.previousModel, JSON.parse(JSON.stringify(this.baseForm.model)));
    }

    this.versionHistoryHideableGrid.toggle(HIDEABLE_GRIDS.coiForm.versionHistory.showOnFirstLoad);

    this.ensurePageActionButtonsAreEnabledOnFormLoad(READ_ONLY_ACCESS_PAGE_ACTION_BUTTONS.coiForm);
    this.handlePostFormLoadActions(event);
  }

  modelChanged(event: any): void {
    this.handleSingleCheckBoxChanges();
    this.handleApproverChange();
    this.handleManagementPlanNavTabChange();
  }

  switchToEdit(event: any): void {
    this.navigate(['/', ROUTE_CONSTANTS.coiForm.prefix, this.id], true);
  }

  delete(event: any): void {
    this.registerSubscription(
      this.dialogService
        .showConfirm(POP_UP_CODES.coiForm.deleteConfirmation)
        .pipe(take(1))
        .subscribe(data => {
          switch (data as DialogResult) {
            case DialogResult.ACCEPTED: {
              this.spinnerService.showSpinner();
              this.registerSubscription(
                this.appDataService.delete(`${API_ENDPOINTS.coiForm.delete}/${this.id}`)
                  .pipe(take(1), this.errorService.errorHandler())
                  .subscribe(data => {
                    this.spinnerService.hideSpinner();
                    const isSuccess = !SERVER_ERROR_POP_UP_CODES.deleteCoiForm.includes(data.message);
                    this.activeDialog = isSuccess ? POP_UP_CODES.coiForm.deleteSuccessful : data.message;
                    this.dialogService.showDialog(this.activeDialog);
                    if (isSuccess) {
                      this.navigateBackToDashboard();
                    }
                  })
              );
              break;
            }
            case DialogResult.REJECTED: {
              break;
            }
          }
        })
    );
  }

  save(event: any) {

    const saveData = (request: CoiFormSaveRequest) => {
      if (this.isExternalRoute && !request.isSubmit) {
        this.registerSubscription(
          this.dialogService
            .showConfirm(POP_UP_CODES.coiForm.externalSaveConfirmation)
            .pipe(take(1))
            .subscribe(data => {
              switch (data as DialogResult) {
                case DialogResult.ACCEPTED: {
                  this.saveData(request);
                  break;
                }
                case DialogResult.REJECTED: {
                  break;
                }
              }
            })
        );
      }
      else {
        this.saveData(request);
      }
    }

    const saveDataRequest: CoiFormSaveRequest = {
      template: this.templateId,
      versionNumber: this.versionNo,
      id: this.id,
      response: event.model,
      expectedLastUpdateTime: event.lastUpdatedTime,
      isSubmit: event.isSubmit,
      recaptchaToken: null // to be set later
    };

    if (!event.isFormValid && event.isSubmit) {
      this.baseForm.markAllFieldsAsTouched();
      if (!this.baseForm.fields[0]?.formControl.valid) {
        this.baseForm.showValidationSummary(true);
        this.dialogService.showDialog(POP_UP_CODES.common.validationError);
      }
      else {
        saveData(saveDataRequest);
      }
    }
    else {
      saveData(saveDataRequest);
    }
  }

  cancel(event: any) {
    this.navigateBackToDashboard();
  }

  dialogSubmitted(event: any) {

    const activeDialog = this.activeDialog;
    delete this.activeDialog;

    switch (activeDialog) {
      case POP_UP_CODES.common.saveSuccessful: {
        this.navigateBackToDashboard(true);
        break;
      }
      case POP_UP_CODES.common.submitSuccessful: {
        if (event?.downloadCopy) {
          this.downloadCopy();
        }
        this.navigateBackToDashboard(true);
        break;
      }
      case POP_UP_CODES.coiForm.reopenWithComment: {
        this.reopenWithComment(event);
        break;
      }
      case POP_UP_CODES.coiForm.reopenSuccessful: {
        this.router.navigate(['/', ROUTE_CONSTANTS.coiForm.prefix, this.id]);
        break;
      }
    }
  }

  actionButtonClicked(event: any): void {
    switch (event.field.key) {
      case QUESTION_CODES.coiForm.versionHistory: {
        switch (event.event.type) {
          case FORM_ACTIONS.coiForm.downloadVersion: {
            this.downloadVersion(event.event.data.id);
            break;
          }
        }
        break;
      }
    }
  }

  private isRecaptchaRequired(): boolean {
    return this.isExternalRoute;
  }

  private navigateBackToDashboard(ignoreUnsavedChanges = false): void {
    if (this.isExternalRoute) {
      this.navigateToEndOfSessionPage(ignoreUnsavedChanges);
    }
    else {
      this.navigate(['/', ROUTE_CONSTANTS.coiForm.list], ignoreUnsavedChanges);
    }
  }

  private saveData(request: CoiFormSaveRequest) {

    const saveDataFunction = (request: CoiFormSaveRequest) => {
      this.spinnerService.showSpinner();
      const saveEndpoint = this.isExternalRoute
        ? API_ENDPOINTS.coiForm.saveExternal
        : API_ENDPOINTS.coiForm.save;
      this.registerSubscription(
        this.dataService.saveData(saveEndpoint, request)
          .pipe(take(1), this.errorService.errorHandler())
          .subscribe(data => {
            this.spinnerService.hideSpinner();
            if (SERVER_ERROR_POP_UP_CODES.saveCoiForm.includes(data.message)) {
              this.activeDialog = data.message;
            }
            else {
              this.id = data.id;
              this.activeDialog = request.isSubmit
                ? POP_UP_CODES.common.submitSuccessful
                : POP_UP_CODES.common.saveSuccessful;
            }
            this.dialogService.showDialog(this.activeDialog);
          })
      );
    };

    if (this.isRecaptchaRequired()) {
      this.recaptchaService.getToken().then(
        token => {
          request.recaptchaToken = token;
          saveDataFunction(request);
        },
        error => console.log('reCAPTCHA service getToken error: ' + error));
    }
    else {
      saveDataFunction(request);
    }
  }

  private downloadCopy(): void {
    const downloadCopyEndpoint = this.isExternalRoute
      ? `${API_ENDPOINTS.coiForm.downloadCopyExternal}/${this.id}`
      : `${API_ENDPOINTS.coiForm.downloadCopy}/${this.id}`;
    this.appDataService.generatePdf(downloadCopyEndpoint);
  }

  private downloadVersion(versionId: string): void {
    const downloadVersionEndpoint = this.isExternalRoute
      ? `${API_ENDPOINTS.coiForm.downloadVersionExternal}/${this.id}/${versionId}`
      : `${API_ENDPOINTS.coiForm.downloadVersion}/${this.id}/${versionId}`
    this.appDataService.generatePdf(downloadVersionEndpoint);
  }

  private openDialogForReopenWithComment(): void {
    this.activeDialog = POP_UP_CODES.coiForm.reopenWithComment;
    this.dialogService.showDialog(this.activeDialog);
  }

  private reopenWithComment(model: any): void {
    this.spinnerService.showSpinner();
    this.registerSubscription(
      this.appDataService.post(API_ENDPOINTS.coiForm.reopenWithComment, { id: this.id, approverReopenComment: model.approverReopenComment })
        .pipe(take(1), this.errorService.errorHandler())
        .subscribe(data => {
          this.spinnerService.hideSpinner();
          this.activeDialog = POP_UP_CODES.coiForm.reopenSuccessful;
          this.dialogService.showDialog(this.activeDialog);
        }));
  }

  private reopen(): void {
    this.registerSubscription(
      this.dialogService
        .showConfirm(POP_UP_CODES.coiForm.reopenConfirmation)
        .pipe(take(1))
        .subscribe(data => {
          switch (data as DialogResult) {
            case DialogResult.ACCEPTED: {
              this.spinnerService.showSpinner();
              this.registerSubscription(
                this.appDataService.post(`${API_ENDPOINTS.coiForm.reopen}/${this.id}`, {})
                  .pipe(take(1), this.errorService.errorHandler())
                  .subscribe(data => {
                    this.spinnerService.hideSpinner();
                    this.activeDialog = POP_UP_CODES.coiForm.reopenSuccessful;
                    this.dialogService.showDialog(this.activeDialog);
                  }));
              break;
            }
            case DialogResult.REJECTED: {
              break;
            }
          }
        })
    );
  }

  private handleSingleCheckBoxChanges(): void {
    const consentQuestionPath: QuestionPath = [
      NAV_TAB_NUMBERS.coiForm.attestation,
      PAGE_NUMBERS.coiForm.attestation,
      SECTION_CODES.coiForm.attestationSection,
      QUESTION_CODES.coiForm.consent
    ];
    this.responseParserService.handleSingleCheckBoxQuestionChanges(
      () => this.baseForm.model,
      model => this.baseForm.model = model,
      [consentQuestionPath]);
  }

  private handleApproverChange(): void {
    const approver = this.getApprover();
    if (this.approver?.email !== approver?.email) {
      this.approver = approver;
      this.setApproverRole(approver?.role);
      this.reloadModel();
    }
  }

  private getApprover(): EntraIdUser {
    const approverQuestionModel: TypeaheadQuestionModel<string> = this.responseParserService.findQuestionModel(
      this.baseForm.model,
      NAV_TAB_NUMBERS.coiForm.yourApprover,
      PAGE_NUMBERS.coiForm.yourApprover,
      null,
      SECTION_CODES.coiForm.yourApproverSection,
      null,
      QUESTION_CODES.coiForm.yourApprover);
    const approver = this.jsonSerialisationService.tryDeserialise<EntraIdUser>(approverQuestionModel?.code);
    return approver;
  }

  private setApproverRole(approveRole: string): void {
    this.responseParserService.replaceQuestionModel(
      this.baseForm.model,
      NAV_TAB_NUMBERS.coiForm.yourApprover,
      PAGE_NUMBERS.coiForm.yourApprover,
      null,
      SECTION_CODES.coiForm.yourApproverSection,
      null,
      QUESTION_CODES.coiForm.approverRole,
      approveRole);
  }

  // The purpose of this method is to ensure that the vertical navigation control does not show the green tick for the 'managementPlan' nav tab
  // when the nav tab is still empty due to the 'yourAssessment' question in the previous nav tab not being answered yet.
  // Since the vertical navigation control uses the AbstractControl.valid property to determine whether to show the green tick, we inject a fake error into the nav tab control
  // in the specific scenario when we don't want the green tick to be shown.
  private handleManagementPlanNavTabChange(): void {

    const isManagementPlanNavTabEmpty = () => {
      // We check whether the 'managementPlan' nav tab is empty by checking whether the 'yourAssessment' question is not answered, due to the existing question conditions.
      // Another, seemingly cleaner, approach would be to check directly whether the 'managementPlan' nav tab model is empty,
      // but even in that case we need to only check the relevant sections / questions, so in the end the current approach is actually simpler.
      const yourAssessmentQuestionModel = this.responseParserService.findQuestionModel(
        this.baseForm.model,
        NAV_TAB_NUMBERS.coiForm.yourDeclaration,
        PAGE_NUMBERS.coiForm.yourDeclaration,
        null,
        SECTION_CODES.coiForm.yourDeclarationSection,
        null,
        QUESTION_CODES.coiForm.yourAssessment);
      const isYourAssessmentQuestionNotAnswered = !yourAssessmentQuestionModel;
      return isYourAssessmentQuestionNotAnswered;
    };

    const isNavTabValid = (navTabControl: AbstractControl) =>
      navTabControl?.valid === true;

    const invalidateNavTab = (navTabControl: AbstractControl) => {
      // Unfortunately, setting directly navTabControl.valid = false does not work, so we need to set a dummy validation error instead.
      navTabControl.setErrors({ dummy2633B1A5246C4850A4717F960ECFFB87: true }); // the trailing GUID is just a random string to avoid conflicts with any real validation error
      this.formDataService.forceRender(navTabControl);
    };

    const managementPlanNavTabControl = this.formConfigurationParserService.findNavTabConfig(this.baseForm.fields, NAV_TAB_NUMBERS.coiForm.managementPlan)?.formControl;
    if (isManagementPlanNavTabEmpty() && isNavTabValid(managementPlanNavTabControl)) {
      invalidateNavTab(managementPlanNavTabControl);
    }
  }
}
